
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import AddResourceAssignmentDialog from "./AddResourceAssignmentDialog";
import EditResourceAssignmentDialog from "./EditResourceAssignmentDialog";
import { Resource } from "../hooks/useResourceManagement";

interface ResourceFormManagerProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  editingAssignment: any;
  setEditingAssignment: (assignment: any) => void;
  resources: Resource[];
  selectedTaskId: string | null;
  getTaskDatesFromAssignments: (taskId: string) => { start_date: string; end_date: string };
  onSaveResource: (formData: any) => Promise<void>;
  existingAssignments?: any[];
}

export interface ResourceFormManagerRef {
  handleAddResource: () => void;
  handleEditResource: (assignment: any) => void;
}

const ResourceFormManager = forwardRef<ResourceFormManagerRef, ResourceFormManagerProps>(({
  isDialogOpen,
  setIsDialogOpen,
  editingAssignment,
  setEditingAssignment,
  resources,
  selectedTaskId,
  getTaskDatesFromAssignments,
  onSaveResource,
  existingAssignments = []
}, ref) => {
  const [formData, setFormData] = useState({
    resource_id: '',
    task_id: '',
    start_date: '',
    end_date: '',
    allocated_percentage: 100,
    rate: 0,
    allocation_hrs: '8',
    duration: 30,
    role: '',
    role_type: ''
  });

  const handleAddResource = () => {
    setEditingAssignment(null);
    const taskDates = selectedTaskId ? getTaskDatesFromAssignments(selectedTaskId) : { start_date: '', end_date: '' };
    
    setFormData({
      resource_id: '',
      task_id: selectedTaskId || '',
      start_date: taskDates.start_date,
      end_date: taskDates.end_date,
      allocated_percentage: 100,
      rate: 0,
      allocation_hrs: '8',
      duration: 30,
      role: '',
      role_type: ''
    });
    setIsDialogOpen(true);
  };

  const handleEditResource = (assignment: any) => {
    setEditingAssignment(assignment);
    
    // Find the resource to get its capacity using the correct UUID
    const selectedResource = resources.find(r => r.id === assignment.resource_id);
    console.log('🔍 EDIT ASSIGNMENT - Finding resource:', {
      assignmentResourceId: assignment.resource_id,
      availableResources: resources.map(r => ({ id: r.id, name: r.name })),
      foundResource: selectedResource
    });
    
    const resourceCapacity = selectedResource?.capacity || assignment.allocation_hrs || '8';
    
    setFormData({
      resource_id: assignment.resource_id,
      task_id: assignment.task_id,
      start_date: assignment.start_date,
      end_date: assignment.end_date,
      allocated_percentage: assignment.allocated_percentage || 100,
      rate: assignment.rate || 0,
      allocation_hrs: assignment.allocation_hrs || resourceCapacity,
      duration: assignment.duration || 30,
      role: assignment.role || selectedResource?.role || '',
      role_type: assignment.role_type || selectedResource?.role_type || ''
    });
    setIsDialogOpen(true);
  };

  const handleResourceChange = (resourceId: string) => {
    const selectedResource = resources.find(r => r.id === resourceId);
    console.log('💾 RESOURCE CHANGE - Selected resource:', {
      resourceId,
      foundResource: selectedResource,
      availableResources: resources.map(r => ({ id: r.id, name: r.name, email: r.email }))
    });
    
    if (selectedResource) {
      setFormData(prev => ({
        ...prev,
        resource_id: resourceId, // Ensure we use the correct UUID from resources table
        rate: selectedResource.rate || 0,
        allocation_hrs: selectedResource.capacity || '8'
      }));
    }
  };

  const handleSave = async () => {
    console.log('💾 SAVE ASSIGNMENT - Form data:', {
      formData,
      selectedResourceId: formData.resource_id,
      matchingResource: resources.find(r => r.id === formData.resource_id)
    });
    await onSaveResource(formData);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingAssignment(null);
  };

  useImperativeHandle(ref, () => ({
    handleAddResource,
    handleEditResource
  }));

  // Render the appropriate dialog based on whether we're editing or adding
  if (editingAssignment) {
    return (
      <EditResourceAssignmentDialog
        isOpen={isDialogOpen}
        onClose={handleClose}
        assignment={editingAssignment}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        resources={resources}
      />
    );
  }

  return (
    <AddResourceAssignmentDialog
      isOpen={isDialogOpen}
      onClose={handleClose}
      formData={formData}
      setFormData={setFormData}
      resources={resources}
      onSave={handleSave}
      onResourceChange={handleResourceChange}
      existingAssignments={existingAssignments}
      selectedTaskId={selectedTaskId}
    />
  );
});

ResourceFormManager.displayName = 'ResourceFormManager';

export default ResourceFormManager;
